import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { photoStore } from './photoStore';
import { FormControl, Button, FormLabel } from 'react-bootstrap';
import Camera, { FACING_MODES } from 'react-html5-camera-photo';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toastr } from 'react-redux-toastr';

import { Itile } from '../../models';
import CommonModal from '../common/CommonModal';
import { constants } from '../../constants/constants';
import { addNewPhoto } from '../../actions/photosActions';
import NewPhotoForm from './NewPhotoForm';
import 'react-html5-camera-photo/build/css/index.css';
import MobileTakePhotoControl from './Mobile/TakePhotoControl';
import HistoricalImagesModal from './HistoricalImagesModal';
import { TFunction } from 'react-i18next';
import { cameraManager } from './CameraSingleton';

import { v4 as uuidv4 } from 'uuid';
const iconCamera = require('../../images/icons/icon-camera.svg').default;
const iconPhoto = require('../../images/icons/icon-photo.svg').default;
const maxPhotoSize = 31457280;
const defaultMaxWidth = 1200;

interface Iprops {
    installBaseID: string;
    loading: boolean;
    t: TFunction;
    currentTile: Itile;
    addNewPhoto: typeof addNewPhoto;
    isMobile: boolean;
}

interface Istate {
    images: { url: string; id: string }[];
    showAddNewPhotoModal: boolean;
    showCamera: boolean;
    showHistoricalImagesModal: boolean;
}

interface ImageData {
    id: string;
    url: string;
}

const resizeImage = async (file: Blob, maxWidth = defaultMaxWidth): Promise<Blob> => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.src = URL.createObjectURL(file);
        img.onload = () => {
            const scaleFactor = maxWidth / img.width;
            const targetWidth = img.width * scaleFactor;
            const targetHeight = img.height * scaleFactor;
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            canvas.width = targetWidth;
            canvas.height = targetHeight;
            ctx?.drawImage(img, 0, 0, targetWidth, targetHeight);
            canvas.toBlob(blob => {
                if (blob) {
                    resolve(blob);
                } else {
                    reject(new Error('Error creating blob from canvas'));
                }
            }, file.type);
        };
        img.onerror = () => {
            reject(new Error('Error loading image'));
        };
    });
};

class TakePhotoControl extends React.PureComponent<Iprops, Istate> {
    static defaultProps = {
        currentTile: constants.getTileByURL(window.location.pathname)
    };

    constructor(props: Iprops) {
        super(props);
        // Restore camera state from singleton manager that persists across component remounts
        const cameraWasOpen = cameraManager.isOpen();
        this.state = {
            images: [],
            showAddNewPhotoModal: false,
            showCamera: cameraWasOpen,
            showHistoricalImagesModal: false
        };
    }

    componentWillUnmount() {
        // Clean up camera state when navigating away from Photos entirely
        // Only hide, don't cleanup the container as other instances might remount
        if (this.state.showCamera) {
            cameraManager.hide();
        }
    }

    getFileUrl = (key: string) => {
        return photoStore.getItem(key).then((item: any) => {
            return window.URL.createObjectURL(item);
        });
    };

    handleTakePhoto = (dataUri: string): void => {
        const base64Length = dataUri.length - (dataUri.indexOf(',') + 1);
        const padding =
            dataUri.charAt(dataUri.length - 2) === '=' ? 2 : dataUri.charAt(dataUri.length - 1) === '=' ? 1 : 0;
        const fileSize = base64Length * 0.75 - padding;

        if (fileSize > maxPhotoSize) {
            toastr.error(this.props.t('toastMessage:invalidFormSubmission'), '', constants.toastrError);
        } else {
            const id = uuidv4();

            fetch(dataUri)
                .then(result => result.blob())
                .then(data => resizeImage(data, defaultMaxWidth))
                .then(resizedBlob => {
                    const resizedFile = new File([resizedBlob], id, { type: resizedBlob.type });
                    return photoStore.setItem(id, resizedFile);
                })
                .then(value => {
                    // Clear camera state when photo taken
                    cameraManager.hide();
                    this.setState({
                        images: [{ id, url: window.URL.createObjectURL(value) }],
                        showCamera: false,
                        showAddNewPhotoModal: true
                    });
                })
                .catch(err => {
                    console.error('[handleTakePhoto]:', err);
                    toastr.error(this.props.t('toastMessage:photoStorageError'), '', constants.toastrError);
                });
        }
    };

    handleCameraError = (error: any): void => {
        // Clear camera state on error
        cameraManager.hide();
        this.setState({ showCamera: false });
        const toastrConfirmOptions = {
            okText: this.props.t('common:ok'),
            disableCancel: true
        };
        toastr.confirm(error.message, toastrConfirmOptions);
    };

    // Handle file change function
    handleFileChange = async (fileList: FileList) => {
        const images: ImageData[] = [];

        for (let i = 0; i < fileList.length; i++) {
            const id = uuidv4();
            const originalFile = fileList[i];

            if (!originalFile) {
                console.error('Error resizing image: no file');
                continue;
            }

            try {
                const resizedBlob = await resizeImage(originalFile, 1200);
                const resizedFile = new File([resizedBlob], originalFile.name, { type: resizedBlob.type });

                await photoStore.setItem(id, resizedFile).then((value: any) => {
                    images.push({ id, url: window.URL.createObjectURL(value) });
                });
            } catch (error) {
                console.error('Error resizing image:', error);
                toastr.error(this.props.t('toastMessage:photoStorageError'), '', constants.toastrError);
            }
        }
        this.setState({ showAddNewPhotoModal: true, images });
    };

    handleCameraOpen = () => {
        cameraManager.show();
        this.setState({ showCamera: true });
    };

    handleCameraClose = () => {
        cameraManager.hide();
        this.setState({ showCamera: false });
    };

    getShowCameraComponent = (handleClose: () => void) => {
        const cameraElement = (
            <div className="camera-wrapper">
                <div className="btn-close" onClick={() => handleClose()}>
                    <FontAwesomeIcon icon={['far', 'times']} size="lg" />
                </div>
                {React.createElement(Camera as any, {
                    isFullscreen: true,
                    isDisplayStartCameraError: false,
                    onTakePhoto: this.handleTakePhoto,
                    onCameraError: this.handleCameraError,
                    idealFacingMode: FACING_MODES.ENVIRONMENT,
                    isImageMirror: false
                })}
            </div>
        );

        // Use portal to render camera to document.body to persist across layout changes
        return ReactDOM.createPortal(cameraElement, document.body);
    };

    filesAreTooBig = (files: FileList, maxSize: number): boolean => {
        for (let i = 0; i < files.length; i++) {
            const size = files.item(i)?.size || 0;
            if (size > maxSize) {
                return true;
            }
        }
        return false;
    };

    getAttachImageComponent = () => {
        return (
            <FormLabel htmlFor="fileUpload" style={{ cursor: 'pointer', margin: 0 }}>
                <span className="btn btn-default" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <img src={iconPhoto} alt="" />
                    {this.props.t('attachImage')}
                </span>
                <FormControl
                    accept="image/png, image/jpeg"
                    id={'fileUpload'}
                    type="file"
                    multiple={true}
                    name="product-image"
                    onChange={(event: any) => {
                        const fileInput = event.target as HTMLInputElement;
                        const files = fileInput.files as FileList;
                        if (this.filesAreTooBig(files, maxPhotoSize)) {
                            toastr.error(this.props.t('toastMessage:invalidFormSubmission'), '', constants.toastrError);
                            return;
                        }
                        this.handleFileChange(files);
                    }}
                    style={{ display: 'none' }}
                />
            </FormLabel>
        );
    };

    render() {
        const { t, isMobile } = this.props;
        const historicImages = (
            <HistoricalImagesModal
                show={this.state.showHistoricalImagesModal}
                hide={() => this.setState({ showHistoricalImagesModal: false })}
                t={t}
            />
        );

        // Hoist camera rendering to parent level to persist across orientation changes
        const cameraComponent = this.state.showCamera && this.getShowCameraComponent(this.handleCameraClose);

        if (isMobile) {
            return (
                <>
                    <MobileTakePhotoControl
                        images={this.state.images}
                        getShowCameraComponent={this.getShowCameraComponent}
                        getAttachImageComponent={this.getAttachImageComponent}
                        showCamera={this.state.showCamera}
                        showAddNewPhotoModal={this.state.showAddNewPhotoModal}
                        onAddNewModalHide={() => this.setState({ showAddNewPhotoModal: false })}
                        handleCameraClose={this.handleCameraClose}
                        handleCameraOpen={this.handleCameraOpen}
                        t={t}
                        addNewPhoto={this.props.addNewPhoto}
                        installBaseID={this.props.installBaseID}
                        showHistoricalImagesModal={() => this.setState({ showHistoricalImagesModal: true })}
                    />
                    {cameraComponent}
                    {historicImages}
                </>
            );
        }

        return (
            <div className="take-photo-control">
                <Button variant="primary" onClick={this.handleCameraOpen}>
                    <img src={iconCamera} alt="" />
                    {t('addNewImage')}
                </Button>
                {cameraComponent}
                {this.getAttachImageComponent()}
                <Button
                    variant="primary"
                    style={{ marginLeft: '016px' }}
                    onClick={() => this.setState({ showHistoricalImagesModal: true })}
                >
                    {t('historicalImagesButton')}
                </Button>
                {historicImages}
                <CommonModal
                    className="job-edit add-photo-modal"
                    show={this.state.showAddNewPhotoModal}
                    onHide={() => this.setState({ showAddNewPhotoModal: false })}
                    title="Edit Image"
                    container={document.getElementById('two-pane-layout')}
                >
                    <NewPhotoForm
                        images={this.state.images}
                        t={t}
                        loading={this.props.loading}
                        installBaseID={this.props.installBaseID}
                        addNewPhoto={this.props.addNewPhoto}
                        toggleModal={() => this.setState({ showAddNewPhotoModal: false })}
                    />
                </CommonModal>
            </div>
        );
    }
}
export default TakePhotoControl;
